import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
  Alert,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { SubjectRouteParams } from '../types';
import { useSubject } from '../../domain/hooks/useSubjects';
import { trpc } from '@/utils/trpc';
import { notificationService } from '@shared/components/notifications';

export const SubjectSettingsTab: React.FC = () => {
  const { tenantId, domainId, subjectId } = useParams<SubjectRouteParams>();
  const utils = trpc.useUtils();

  const {
    data: subject,
    isLoading,
    isError,
  } = useSubject(tenantId!, subjectId!);

  const updateMutation = trpc.subject.update.useMutation({
    onSuccess: (updatedSubject) => {
      if (updatedSubject) {
        utils.subject.getById.invalidate({
          tenantId: updatedSubject.tenantId,
          subjectId: updatedSubject.subjectId,
        });
        utils.subject.listByDomain.invalidate({
          tenantId: tenantId!,
          domainId: domainId!,
        });
        notificationService.success('Settings updated successfully');
      }
    },
    onError: (error) => {
      notificationService.error(error?.message || 'Failed to update settings');
    },
  });

  const handleToggleEmailNotifications = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    updateMutation.mutate({
      tenantId: tenantId!,
      id: subjectId!,
      enableReportEmailNotifications: checked,
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !subject) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load subject settings. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 600 }}>
      <Card variant="outlined">
        <CardHeader title="Email Notifications" />
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={subject.enableReportEmailNotifications ?? false}
                onChange={handleToggleEmailNotifications}
                disabled={updateMutation.isPending}
              />
            }
            label="Notify professors when reports are generated"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 6 }}>
            When enabled, professors assigned to this subject will receive an email
            notification each time a survey report is automatically generated.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
